import { makeObservable, override, flow } from 'mobx';
import { RootStore } from './RootStore';
import { Opportunity } from './models';
import { ServerOpportunity, IServerOpportunity } from './models/server/ServerOpportunity';
import { getSupplementalDataRequest } from './models/SupplementalOrganizationData';
import { DataStore } from './DataStore';

enum OpportunityRequestType {
    GetInformation = 'get-opp-information',
    PostOpportunity = 'post-opp',
    UpdateInformation = 'update-opp-information',
    DeleteOpportunity = 'delete-opp'
}

type OpportunityResponse = { opportunity: IServerOpportunity }; //, warnings: Array<{ code: number }> };

export class OpportunityStore extends DataStore<OpportunityRequestType> {

    constructor(rootStore: RootStore) {
        super(rootStore);
        makeObservable(this);
    }

    /*  ------------ API Methods ------------  */

    @override get requestURLs() {
        const opportunitiesAPI = '/api/opportunities';
        return {
            [OpportunityRequestType.GetInformation]: (data: any, params: { id: number }) => `${opportunitiesAPI}/${params.id}`,
            [OpportunityRequestType.PostOpportunity]: () => `${opportunitiesAPI}`,
            [OpportunityRequestType.UpdateInformation]: (data: any, params: { id: number }) => `${opportunitiesAPI}/${params.id}`,
            [OpportunityRequestType.DeleteOpportunity]: (data: any, params: { id: number }) => `${opportunitiesAPI}/${params.id}`,
        };
    }

    @override get getRequests() {
        return [
            OpportunityRequestType.GetInformation,
        ];
    }

    @override get postRequests() {
        return [
            OpportunityRequestType.PostOpportunity
        ];
    }

    @override get putRequests() {
        return [
            OpportunityRequestType.UpdateInformation
        ];
    }

    @override get patchRequests() {
        return [];
    }

    @override get deleteRequests() {
        return [OpportunityRequestType.DeleteOpportunity];
    }

    /*  ------------ API Method Implementations ------------  */

    /*************** Get opportunity details ***************/

    getOpportunity = flow(function* (this: OpportunityStore, opportunityId: number, postingOrganizationId?: number) {
        try {
            let isOwnerOfOpportunity: boolean;

            if (postingOrganizationId === undefined) {
                isOwnerOfOpportunity = true;
            } else {
                isOwnerOfOpportunity = postingOrganizationId === this.rootStore.userStore.user.organization?.id;
            }

            const supplementalData = isOwnerOfOpportunity
                ? getSupplementalDataRequest(
                    ['locations', 'serviceEntryTags']
                )
                : undefined

            const data = supplementalData ? { supplementalData } : undefined;

            const response: OpportunityResponse = yield this.makeRequest<OpportunityResponse>(
                OpportunityRequestType.GetInformation,
                data,
                { id: opportunityId },
                true
            );
            return new ServerOpportunity(response.opportunity).deserialize();
        } catch (error) {
            console.log(error);
        }
    });

    /*************** Confirm opportunity edits ***************/

    confirmEdits(organizationId: number, opportunity: Opportunity) {
        if (opportunity.id < 0) {
            return this.addOrUpdateOpportunity(organizationId, opportunity, OpportunityRequestType.PostOpportunity);
        } else {
            return this.addOrUpdateOpportunity(organizationId, opportunity, OpportunityRequestType.UpdateInformation);
        }
    }

    /*************** Post or update opportunity information ***************/

    private addOrUpdateOpportunity = flow(function* (this: OpportunityStore, organizationId: number, opportunity: Opportunity, reqType: OpportunityRequestType) {
        opportunity.setAllFieldsDirty();
        if (opportunity.validated) {
            const response: OpportunityResponse = yield this.makeRequest<OpportunityResponse>(
                reqType,
                {
                    organization: {
                        id: organizationId
                    },
                    opportunity: opportunity.serialize()
                },
                { id: opportunity.id }
            );
            return {
                ...response,
                opportunity: new ServerOpportunity(response.opportunity).deserialize()
            }
        } else {
            return opportunity.validationErrors;
        }
    });

    /*************** Delete opportunity ***************/

    deleteOpportunity = flow(function* (this: OpportunityStore, opportunityId: number) {
        try {
            const organizationId = this.rootStore.userStore.user.organization?.id;
            if (organizationId) {
                const response: OpportunityResponse = yield this.makeRequest<OpportunityResponse>(
                    OpportunityRequestType.DeleteOpportunity,
                    {},
                    { id: opportunityId }
                );
                if (response.opportunity) {
                    return { success: true };
                }
            }
        } catch (error) {
            console.log(error);
        }
    });
}